import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  notFound,
  redirect,
  type AnyRoute,
} from '@tanstack/react-router';
import { visibleSections, type AdminSection } from './admin/sections';
import { AdminHome } from './admin/admin-home';
import { AdminLayout } from './admin/admin-layout';
import { checkDashboard } from './dashboard/tiles';
import { checkRecordSections } from './record-sections/record-sections';
import { checkGuides } from './guides/guides';
import { ApiReferencePage } from './guides/api-reference-page';
import { GuidePage, HelpPage, validateHelpSearch } from './guides/help-page';
import { ApiError } from './api/client';
import { safePath } from './lib/paths';
import type { WebModule } from './modules/define';
import {
  catalogPagePath,
  catalogsOfModule,
  hasSettingsPage,
  kernelCatalogsOfModule,
  manageableKernelCatalogs,
  moduleSettingsPath,
} from './modules/settings';
import {
  CrashPage,
  ForbiddenError,
  ForbiddenPage,
  NotFoundPage,
  PagePending,
} from './pages/error-pages';
import { HomePage } from './pages/home-page';
import { LoginPage, type LoginSearch } from './pages/login-page';
import { ModuleSettingsPage } from './pages/module-settings-page';
import { PasswordLinkPage } from './pages/password-link-page';
import { ProfilePage } from './pages/profile-page';
import { SearchPage, validateSearchParams } from './search/search-page';
import { restoreOfflineSession } from './offline/offline';
import { canIn, sessionQuery } from './session/session';
import { settingsQuery } from './session/settings';
import { AppLayout } from './shell/app-layout';
import { RootLayout } from './shell/root-layout';
import { ForMePage } from './for-me/for-me-page';
import { WhatsNewPage } from './whats-new/whats-new-page';
import { hasQuestionnaires } from './questionnaires/api';
import { documentsOfModule } from './documents/api';
import { checkDocuments } from './documents/providers';

/** Errors of a page: missing permission and missing records get their own page, not a crash. */
function AppError({ error }: { error: unknown }) {
  if (error instanceof ForbiddenError || (error instanceof ApiError && error.status === 403)) {
    return <ForbiddenPage />;
  }
  if (error instanceof ApiError && error.status === 404) return <NotFoundPage />;
  return <CrashPage error={error} />;
}

export interface RouterContext {
  queryClient: QueryClient;
  modules: readonly WebModule[];
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function createAppRouter(context: RouterContext) {
  const root = createRootRouteWithContext<RouterContext>()({
    component: RootLayout,
    notFoundComponent: NotFoundPage,
  });

  const login = createRoute({
    getParentRoute: () => root,
    path: '/login',
    validateSearch: (search: Record<string, unknown>): LoginSearch => ({
      error: text(search.error),
      company: text(search.company),
      returnTo: safePath(search.returnTo),
      expired: search.expired === true || search.expired === 'true' || undefined,
      passwordSet: search.passwordSet === true || search.passwordSet === 'true' || undefined,
    }),
    component: LoginPage,
  });

  // Targets of the password links by email (ADR-0041), without session like the login.
  const setPassword = createRoute({
    getParentRoute: () => root,
    path: '/set-password',
    component: () => <PasswordLinkPage kind="invite" />,
  });
  const resetPassword = createRoute({
    getParentRoute: () => root,
    path: '/reset-password',
    component: () => <PasswordLinkPage kind="reset" />,
  });

  // Everything below needs a session; without one the login page opens.
  const app = createRoute({
    getParentRoute: () => root,
    id: 'app',
    beforeLoad: async ({ context: { queryClient }, location }) => {
      try {
        await Promise.all([
          // A page change reloads an older session in the background, so switched modules show.
          queryClient.ensureQueryData({ ...sessionQuery, revalidateIfStale: true }),
          queryClient.ensureQueryData(settingsQuery),
        ]);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router API
          throw redirect({ to: '/login', search: { returnTo: location.href } });
        }
        // Started without network: the offline data of the last sync carries the app (ADR-0013).
        if (error instanceof TypeError && (await restoreOfflineSession(queryClient))) return;
        throw error;
      }
    },
    component: AppLayout,
    errorComponent: AppError,
  });

  const home = createRoute({ getParentRoute: () => app, path: '/', component: HomePage });

  // Target after login: the personal start page (setting core.startPage).
  const start = createRoute({
    getParentRoute: () => app,
    path: '/start',
    beforeLoad: ({ context: { queryClient } }) => {
      const target = safePath(
        queryClient.getQueryData(settingsQuery.queryKey)?.values['core.startPage'],
      );
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router API
      throw redirect({ to: target && target !== '/start' ? target : '/', replace: true });
    },
  });

  const profile = createRoute({
    getParentRoute: () => app,
    path: '/profile',
    component: ProfilePage,
  });

  const search = createRoute({
    getParentRoute: () => app,
    path: '/search',
    validateSearch: validateSearchParams,
    component: SearchPage,
  });

  const forMe = createRoute({
    getParentRoute: () => app,
    path: '/for-me',
    component: ForMePage,
  });
  // Former pages "Meine Genehmigungen" and "Meine Wiedervorlagen", kept for bookmarks.
  const formerPages = ['/approvals', '/reminders'].map((path) =>
    createRoute({
      getParentRoute: () => app,
      path,
      beforeLoad: () => {
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router API
        throw redirect({ to: '/for-me', replace: true });
      },
    }),
  );

  const whatsNew = createRoute({
    getParentRoute: () => app,
    path: '/whats-new',
    component: WhatsNewPage,
  });

  const help = createRoute({
    getParentRoute: () => app,
    path: '/help',
    validateSearch: validateHelpSearch,
    component: HelpPage,
  });
  const apiReference = createRoute({
    getParentRoute: () => app,
    path: '/help/api',
    component: ApiReferencePage,
  });
  const guide = createRoute({
    getParentRoute: () => app,
    path: '/help/$guideId',
    component: GuidePage,
  });

  // Administration: every page loads on first visit and checks its section first.
  const admin = createRoute({
    getParentRoute: () => app,
    path: '/admin',
    component: AdminLayout,
    errorComponent: AppError,
  });
  const adminHome = createRoute({ getParentRoute: () => admin, path: '/', component: AdminHome });
  const adminPage = (
    section: AdminSection['id'],
    path: string,
    component: ReturnType<typeof lazyRouteComponent>,
  ) =>
    createRoute({
      getParentRoute: () => admin,
      path,
      component,
      beforeLoad: () => {
        const session = context.queryClient.getQueryData(sessionQuery.queryKey);
        if (!session || !visibleSections(session, context.modules).some((s) => s.id === section)) {
          throw new ForbiddenError();
        }
      },
    });
  const accounts = () => import('./admin/accounts-page');
  const account = () => import('./admin/account-page');
  const groups = () => import('./admin/groups-page');
  const group = () => import('./admin/group-page');
  const roles = () => import('./admin/roles-page');
  const role = () => import('./admin/role-page');
  const report = () => import('./admin/roles-report-page');
  const audit = () => import('./admin/audit-page');
  const adminPages = [
    adminPage('accounts', 'accounts', lazyRouteComponent(accounts, 'AccountsPage')),
    adminPage('accounts', 'accounts/$accountId', lazyRouteComponent(account, 'AccountPage')),
    adminPage('groups', 'groups', lazyRouteComponent(groups, 'GroupsPage')),
    adminPage('groups', 'groups/$groupId', lazyRouteComponent(group, 'GroupPage')),
    adminPage('roles', 'roles', lazyRouteComponent(roles, 'RolesPage')),
    adminPage('roles', 'roles/new', lazyRouteComponent(role, 'RolePage')),
    adminPage('roles', 'roles/report', lazyRouteComponent(report, 'RolesReportPage')),
    adminPage('roles', 'roles/$roleId', lazyRouteComponent(role, 'RolePage')),
    adminPage(
      'settings',
      'settings',
      lazyRouteComponent(() => import('./admin/settings-page'), 'AdminSettingsPage'),
    ),
    adminPage(
      'companyModules',
      'modules',
      lazyRouteComponent(() => import('./admin/company-modules-page'), 'CompanyModulesPage'),
    ),
    adminPage(
      'sso',
      'sso',
      lazyRouteComponent(() => import('./admin/sso-page'), 'SsoPage'),
    ),
    adminPage(
      'notifications',
      'notifications',
      lazyRouteComponent(() => import('./admin/notifications-page'), 'NotificationsPage'),
    ),
    adminPage(
      'integrations',
      'integrations',
      lazyRouteComponent(() => import('./admin/integrations-page'), 'IntegrationsPage'),
    ),
    adminPage(
      'customFields',
      'custom-fields',
      lazyRouteComponent(() => import('./admin/custom-fields-page'), 'CustomFieldsPage'),
    ),
    adminPage(
      'shares',
      'shares',
      lazyRouteComponent(() => import('./admin/shares-page'), 'SharesPage'),
    ),
    adminPage(
      'ai',
      'ai',
      lazyRouteComponent(() => import('./admin/ai-page'), 'AiPage'),
    ),
    adminPage(
      'privacy',
      'privacy',
      lazyRouteComponent(() => import('./admin/privacy-page'), 'PrivacyPage'),
    ),
    adminPage(
      'branding',
      'branding',
      lazyRouteComponent(() => import('./admin/branding-page'), 'BrandingPage'),
    ),
    adminPage('audit', 'audit', lazyRouteComponent(audit, 'CompanyAuditPage')),
    adminPage(
      'companies',
      'platform/companies',
      lazyRouteComponent(() => import('./admin/companies-page'), 'CompaniesPage'),
    ),
    adminPage(
      'modules',
      'platform/modules',
      lazyRouteComponent(() => import('./admin/modules-page'), 'ModulesPage'),
    ),
    adminPage(
      'platformAccess',
      'platform/access',
      lazyRouteComponent(() => import('./admin/platform-access-page'), 'PlatformAccessPage'),
    ),
    adminPage('platformAudit', 'platform/audit', lazyRouteComponent(audit, 'PlatformAuditPage')),
    adminPage(
      'operations',
      'platform/operations',
      lazyRouteComponent(() => import('./admin/operations-page'), 'OperationsPage'),
    ),
  ];

  // Former Verwaltung › Mängel, kept for bookmarks: statuses and priorities of findings now sit
  // on the settings page of every module with findings, the first one opens.
  const formerFindings = ['findings', 'findings/$key'].map((path) =>
    createRoute({
      getParentRoute: () => admin,
      path,
      beforeLoad: ({ params }) => {
        const session = context.queryClient.getQueryData(sessionQuery.queryKey);
        const manifests = context.modules.map((m) => m.manifest);
        const module =
          session &&
          manifests.find(
            (m) => !m.parent && manageableKernelCatalogs(m, manifests, session).length > 0,
          );
        if (!module) throw new ForbiddenError();
        const { key } = params as { key?: string };
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router API
        throw redirect({
          to: key ? catalogPagePath(module.id, key) : moduleSettingsPath(module.id),
          replace: true,
        });
      },
    }),
  );

  /** Pages of a module open only while it is active for the company (and its parent too). */
  function requireActive(module: WebModule, permission?: string) {
    const session = context.queryClient.getQueryData(sessionQuery.queryKey);
    const id = module.manifest.id;
    const active =
      session?.modules.includes(id) &&
      (!module.manifest.parent || session.modules.includes(module.manifest.parent));
    if (!session || !active || (permission && !canIn(session, permission))) {
      throw new ForbiddenError();
    }
  }

  checkGuides(context.modules);
  checkDashboard(context.modules);
  checkRecordSections(context.modules);
  checkDocuments(context.modules);
  const modulePages: AnyRoute[] = context.modules.flatMap((module) =>
    module.pages.map((page) => {
      const reserved = moduleSettingsPath(module.manifest.id);
      if (page.path === reserved || page.path.startsWith(`${reserved}/`)) {
        throw new Error(`${page.path}: the settings page of a module belongs to the kernel`);
      }
      return createRoute({
        getParentRoute: () => app,
        path: page.path,
        component: lazyRouteComponent(page.load),
        beforeLoad: () => requireActive(module, page.permission),
      });
    }),
  );

  // Settings page of every main module whose own or submodule manifests declare settings or
  // number sequences.
  const manifests = context.modules.map((m) => m.manifest);
  const moduleSettings: AnyRoute[] = context.modules
    .filter((module) => !module.manifest.parent && hasSettingsPage(module.manifest, manifests))
    .map((module) =>
      createRoute({
        getParentRoute: () => app,
        path: moduleSettingsPath(module.manifest.id),
        component: () => <ModuleSettingsPage moduleId={module.manifest.id} />,
        beforeLoad: () => requireActive(module),
      }),
    );

  // Catalog pages below the settings page of every main module with catalogs, the kernel catalogs
  // of findings included.
  const CatalogPage = lazyRouteComponent(
    () => import('./catalogs/catalog-page'),
    'ModuleCatalogPage',
  );
  const moduleCatalogs: AnyRoute[] = context.modules
    .filter((module) => !module.manifest.parent)
    .map((module) => ({
      module,
      catalogs: [
        ...kernelCatalogsOfModule(module.manifest, manifests),
        ...catalogsOfModule(module.manifest, manifests),
      ],
    }))
    .filter(({ catalogs }) => catalogs.length > 0)
    .map(({ module, catalogs }) =>
      createRoute({
        getParentRoute: () => app,
        path: `${moduleSettingsPath(module.manifest.id)}/catalogs/$key`,
        component: Object.assign(() => <CatalogPage moduleId={module.manifest.id} />, {
          preload: () => CatalogPage.preload?.(),
        }),
        beforeLoad: ({ params }) => {
          requireActive(module);
          const { key } = params as { key: string };
          // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router API
          if (!catalogs.some((c) => c.key === key)) throw notFound();
        },
      }),
    );

  // Editor of the questionnaire templates of a module and its submodules. `preload` lets the
  // router load the page before it renders, as for the other lazy pages.
  const QuestionnaireEditor = lazyRouteComponent(
    () => import('./questionnaires/editor-page'),
    'QuestionnaireEditorPage',
  );
  const questionnaireEditors: AnyRoute[] = context.modules
    .filter((module) => !module.manifest.parent && hasQuestionnaires(module.manifest, manifests))
    .map((module) =>
      createRoute({
        getParentRoute: () => app,
        path: `${moduleSettingsPath(module.manifest.id)}/questionnaires/$templateId`,
        component: Object.assign(() => <QuestionnaireEditor moduleId={module.manifest.id} />, {
          preload: () => QuestionnaireEditor.preload?.(),
        }),
        beforeLoad: () => requireActive(module),
      }),
    );

  // Editor of a document template below the settings page, for `<module>.settings.manage` of
  // the main module or a submodule with documents (docs/architecture/documents.md).
  const TemplateEditor = lazyRouteComponent(
    () => import('./documents/template-editor-page'),
    'TemplateEditorPage',
  );
  const templateEditors: AnyRoute[] = context.modules
    .filter(
      (module) =>
        !module.manifest.parent &&
        manifests.some(
          (m) =>
            (m.id === module.manifest.id || m.parent === module.manifest.id) &&
            (m.documents ?? []).length > 0,
        ),
    )
    .map((module) =>
      createRoute({
        getParentRoute: () => app,
        path: `${moduleSettingsPath(module.manifest.id)}/documents/$templateId`,
        component: Object.assign(() => <TemplateEditor moduleId={module.manifest.id} />, {
          preload: () => TemplateEditor.preload?.(),
        }),
        beforeLoad: () => {
          requireActive(module);
          const session = context.queryClient.getQueryData(sessionQuery.queryKey);
          if (!session || documentsOfModule(module.manifest, manifests, session).length === 0) {
            throw new ForbiddenError();
          }
        },
      }),
    );

  const tree = root.addChildren([
    login,
    setPassword,
    resetPassword,
    app.addChildren([
      home,
      start,
      profile,
      search,
      forMe,
      ...formerPages,
      whatsNew,
      help,
      apiReference,
      guide,
      admin.addChildren([adminHome, ...adminPages, ...formerFindings]),
      ...modulePages,
      ...moduleSettings,
      ...moduleCatalogs,
      ...questionnaireEditors,
      ...templateEditors,
    ]),
  ]);

  return createRouter({
    routeTree: tree,
    context,
    defaultPreload: 'intent',
    defaultPendingMinMs: 0,
    scrollRestoration: true,
    defaultNotFoundComponent: NotFoundPage,
    defaultPendingComponent: PagePending,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;
